import { put, takeLatest } from "redux-saga/effects";
import { PayloadAction } from "@reduxjs/toolkit";
import { httpGet, httpPost, httpPut, httpDelete } from "../../services";
import { resolveWrapperFieldError, WrapperFieldErrorRule } from "../../utils/wrapperFieldErrors";
import {
  OAuthIntegrationSchema,
  getOAuthIntegrationsRequest,
  getOAuthIntegrationsSuccess,
  getOAuthIntegrationsFailure,
  createOAuthIntegrationRequest,
  createOAuthIntegrationSuccess,
  createOAuthIntegrationFailure,
  updateOAuthIntegrationRequest,
  updateOAuthIntegrationSuccess,
  updateOAuthIntegrationFailure,
  deleteOAuthIntegrationRequest,
  deleteOAuthIntegrationSuccess,
  deleteOAuthIntegrationFailure,
  rotateOAuthIntegrationCredentialsRequest,
  rotateOAuthIntegrationCredentialsSuccess,
  rotateOAuthIntegrationCredentialsFailure,
  OAuthIntegrationWrapperPayload,
  createOAuthIntegrationWrapperRequest,
  createOAuthIntegrationWrapperFailure
} from "../features/integration/oauthIntegrations";
import { OAuthCatalogueTemplate, getOAuthCatalogueRequest, getOAuthCatalogueSuccess, getOAuthCatalogueFailure } from "../features/integration/oauthCatalogue";

const BASE = "/platform-configuration/oauth-integration";

// Core / backend error envelope: { ErrorCode, Description, Message }.
const throwIfError = (response: any, fallback: string) => {
  if (response?.ErrorCode) {
    throw new Error(response.Description || response.Message || fallback);
  }
};

// Immutable/server-owned fields must never be sent on update.
const toUpdateBody = (payload: OAuthIntegrationSchema): Partial<OAuthIntegrationSchema> => {
  const { Id, ClientId, ClientSecret, ...body } = payload;
  return body;
};

function* getOAuthIntegrations() {
  try {
    const response = yield httpGet(BASE + "/all");
    throwIfError(response, "Unable to load OAuth integrations.");
    yield put(getOAuthIntegrationsSuccess(response?.Data || []));
  } catch (e: any) {
    yield put(getOAuthIntegrationsFailure(e.message));
  }
}

function* createOAuthIntegration(action: PayloadAction<OAuthIntegrationSchema>) {
  try {
    const response = yield httpPost(BASE, {}, action.payload);
    throwIfError(response, "Unable to create OAuth integration.");
    yield put(createOAuthIntegrationSuccess(response));
  } catch (e: any) {
    yield put(createOAuthIntegrationFailure(e.message));
  }
}

// Core returns a single collection of vendor templates (no OAuth/OIDC partitioning).
function* getOAuthCatalogue() {
  try {
    const response = yield httpGet("/platform-configuration/available-oauth-integrations");
    throwIfError(response, "Unable to load the prebuilt OAuth integration catalogue.");
    const templates: OAuthCatalogueTemplate[] = response?.FederatedSSOIntegrationsOAuth || [];
    yield put(getOAuthCatalogueSuccess(templates));
  } catch (e: any) {
    yield put(getOAuthCatalogueFailure(e.message));
  }
}

/**
 * OAuth/OIDC has no local description->field rules.
 *
 * Every 8253 cause that named a field — DisplayName required, the 60-character name limit,
 * RedirectURI required, and an invalid RedirectURI — is rejected by the form itself, using the same
 * rules Core applies (see OAuthPrebuiltForm's oauthFieldValidators). Matching backend prose to
 * fields was brittle by construction: a reworded Core message silently stopped attributing, and the
 * table had to be extended by hand for every field a future template might declare.
 *
 * The shared resolver is still used, so Core's own attribution keeps working: apiError.Errors is
 * []ValidationError{FieldName, ErrorMessage}. It is empty on this route today, so errors show as
 * global toasts — correct for the codes that still reach here, none of which name a field
 * (MISSING_FIELDS_IN_PREBUILT, INVALID_OAUTH_INTEGRATION_TYPE, LIMIT_EXCEEDED, feature/permission)
 * — and inline attribution resumes on its own if Core starts populating Errors.
 *
 * SAML and Custom IdP still carry rule tables, because their attributable errors are server-only
 * facts a form cannot pre-empt (a duplicate name, a reserved name, an unparseable certificate).
 */
const OAUTH_WRAPPER_FIELD_ERRORS: WrapperFieldErrorRule[] = [];

// Reuses createOAuthIntegrationSuccess so the one-time ClientSecret, the edit-in-place
// transition and the list update are shared with the custom create path, not duplicated.
function* createOAuthIntegrationWrapper(action: PayloadAction<OAuthIntegrationWrapperPayload>) {
  try {
    const response = yield httpPost("/platform-configuration/integration/oauth", {}, action.payload);
    if (response?.ErrorCode) {
      const submittedFields = Object.keys(action.payload).filter((key) => key !== "IntegrationType");
      yield put(
        createOAuthIntegrationWrapperFailure({
          // Preserve Core's own message so specific failures (notably the tenant SSO
          // integration limit) reach the user instead of a generic fallback.
          message: response.Description || response.Message || "Unable to create OAuth integration.",
          fieldErrors: resolveWrapperFieldError(response, submittedFields, OAUTH_WRAPPER_FIELD_ERRORS)
        })
      );
      return;
    }
    yield put(createOAuthIntegrationSuccess(response));
  } catch (e: any) {
    yield put(createOAuthIntegrationWrapperFailure({ message: e.message }));
  }
}

function* updateOAuthIntegration(action: PayloadAction<OAuthIntegrationSchema>) {
  try {
    const id = action.payload.Id || "";
    const response = yield httpPut(BASE + "/" + encodeURIComponent(id), {}, toUpdateBody(action.payload));
    throwIfError(response, "Unable to update OAuth integration.");
    yield put(updateOAuthIntegrationSuccess(response));
  } catch (e: any) {
    yield put(updateOAuthIntegrationFailure(e.message));
  }
}

function* deleteOAuthIntegration(action: PayloadAction<{ Id: string; DisplayName?: string }>) {
  try {
    const response = yield httpDelete(BASE + "/" + encodeURIComponent(action.payload.Id), {});
    throwIfError(response, "Unable to delete OAuth integration.");
    yield put(deleteOAuthIntegrationSuccess({ Id: action.payload.Id }));
  } catch (e: any) {
    yield put(deleteOAuthIntegrationFailure(e.message));
  }
}

function* rotateOAuthIntegrationCredentials(action: PayloadAction<{ Id: string }>) {
  try {
    const response = yield httpPut(BASE + "/" + encodeURIComponent(action.payload.Id) + "/credentials", {}, {});
    throwIfError(response, "Unable to regenerate the client secret.");
    yield put(rotateOAuthIntegrationCredentialsSuccess({ ClientId: response.ClientId, ClientSecret: response.ClientSecret }));
  } catch (e: any) {
    yield put(rotateOAuthIntegrationCredentialsFailure(e.message));
  }
}

export function* watchGetOAuthIntegrations() {
  yield takeLatest(getOAuthIntegrationsRequest.type, getOAuthIntegrations);
}

export function* watchCreateOAuthIntegration() {
  yield takeLatest(createOAuthIntegrationRequest.type, createOAuthIntegration);
}

export function* watchGetOAuthCatalogue() {
  yield takeLatest(getOAuthCatalogueRequest.type, getOAuthCatalogue);
}

export function* watchCreateOAuthIntegrationWrapper() {
  yield takeLatest(createOAuthIntegrationWrapperRequest.type, createOAuthIntegrationWrapper);
}

export function* watchUpdateOAuthIntegration() {
  yield takeLatest(updateOAuthIntegrationRequest.type, updateOAuthIntegration);
}

export function* watchDeleteOAuthIntegration() {
  yield takeLatest(deleteOAuthIntegrationRequest.type, deleteOAuthIntegration);
}

export function* watchRotateOAuthIntegrationCredentials() {
  yield takeLatest(rotateOAuthIntegrationCredentialsRequest.type, rotateOAuthIntegrationCredentials);
}
